import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, TrendingUp, TrendingDown } from "lucide-react";
import { useState } from "react";

interface ExchangeRate {
  currency: string;
  rate: number;
  change: number;
  changePercent: number;
}

export default function PopularExchangeRates() {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: rates, refetch, isLoading } = useQuery({
    queryKey: ["/api/forex-rates/today"],
    queryFn: async () => {
      const response = await fetch("/api/forex-rates/today");
      if (!response.ok) throw new Error("Failed to fetch rates");
      return response.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Convert our forex rates to display format - show most popular currencies
  const displayRates: ExchangeRate[] = rates ? [
    { currency: "USD/INR", rate: parseFloat(rates.find(r => (r.currencyCode || r.currency_code) === "USD")?.rate || "84.25"), change: 0.21, changePercent: 0.25 },
    { currency: "EUR/INR", rate: parseFloat(rates.find(r => (r.currencyCode || r.currency_code) === "EUR")?.rate || "91.80"), change: 0.17, changePercent: 0.18 },
    { currency: "GBP/INR", rate: parseFloat(rates.find(r => (r.currencyCode || r.currency_code) === "GBP")?.rate || "105.20"), change: -0.13, changePercent: -0.12 },
    { currency: "AED/INR", rate: parseFloat(rates.find(r => (r.currencyCode || r.currency_code) === "AED")?.rate || "22.95"), change: 0.07, changePercent: 0.31 },
    { currency: "CAD/INR", rate: parseFloat(rates.find(r => (r.currencyCode || r.currency_code) === "CAD")?.rate || "63.90"), change: 0.12, changePercent: 0.19 },
    { currency: "SGD/INR", rate: parseFloat(rates.find(r => (r.currencyCode || r.currency_code) === "SGD")?.rate || "63.20"), change: 0.08, changePercent: 0.13 },
  ] : [];

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl font-semibold">Popular Exchange Rates</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh Rates
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="h-5 bg-gray-300 rounded animate-pulse w-20"></div>
                  <div className="h-6 bg-gray-300 rounded animate-pulse w-12"></div>
                </div>
                <div className="space-y-1">
                  <div className="h-8 bg-gray-300 rounded animate-pulse w-24"></div>
                  <div className="h-4 bg-gray-300 rounded animate-pulse w-16"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {displayRates.map((rate, index) => (
              <div key={rate.currency} className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{rate.currency}</h3>
                  <Badge 
                    variant={rate.changePercent >= 0 ? "default" : "destructive"}
                    className={`text-xs ${rate.changePercent >= 0 ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}
                  >
                    {rate.changePercent >= 0 ? (
                      <TrendingUp className="h-3 w-3 mr-1" />
                    ) : (
                      <TrendingDown className="h-3 w-3 mr-1" />
                    )}
                    {rate.changePercent >= 0 ? '+' : ''}{rate.changePercent.toFixed(2)}%
                  </Badge>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-gray-900">
                    {rate.rate.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-400">Live Rate</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}